import { THoliday, TWorkingDay } from "@harea/component";
import { useMemo } from "react";
import { useWorkingDayListQuery } from "./useWorkingDayListQuery";
import { useOffDayListQuery } from "hooks/useOffDayListQuery";

const toDay = (date?: string) => {
    if (!date) {
        return "";
    }
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
        return date.slice(0, 10);
    }
    return parsedDate.toISOString().slice(0, 10);
};

const buildDateRangeKey = (startDate?: string, endDate?: string) =>
    `${toDay(startDate)}|${toDay(endDate)}`;

const isGroupHoliday = (item: THoliday) => Boolean((item as any)?.personnelGroup?.id);

export const usePreprocessedOffDays = (year: number, personnelId?: number) => {
    const { data: offDaysData } = useOffDayListQuery(true, year, personnelId);
    const { data: workingDaysData } = useWorkingDayListQuery(true, year, personnelId);

    return useMemo(() => {
        const offDays = offDaysData?.data ?? [];
        const workingDays = workingDaysData?.data ?? [];

        const workingDayKeys = new Set(
            workingDays.map((item: TWorkingDay) =>
                buildDateRangeKey(item.startDate, item.endDate)
            )
        );

        const distinctOffDaysMap = new Map<string, THoliday>();

        for (const offDay of offDays) {
            const key = buildDateRangeKey(offDay.startDate, offDay.endDate);

            // WorkingDay has the highest priority, so matching OffDay is ignored.
            if (workingDayKeys.has(key)) {
                continue;
            }

            const existing = distinctOffDaysMap.get(key);
            if (!existing) {
                distinctOffDaysMap.set(key, offDay);
                continue;
            }

            // For same date range: group OffDay overrides public OffDay.
            if (!isGroupHoliday(existing) && isGroupHoliday(offDay)) {
                distinctOffDaysMap.set(key, offDay);
            }
        }

        return {
            workingDays,
            offDays: Array.from(distinctOffDaysMap.values())
        }
    }, [offDaysData?.data, workingDaysData?.data]);
};
